import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal} from "@angular/core";
import {Meta, Title} from "@angular/platform-browser";
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet} from "@angular/router";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {datadogLogs} from "@datadog/browser-logs";
import {datadogRum} from "@datadog/browser-rum";
import {environment} from "@env/environment";
import {filter, map, switchMap, tap} from "rxjs/operators";

import {resolvePluginFromPath} from "./core/datadog-plugin-context";
import {I18nService} from "./core/i18n.service";
import {Logger} from "./core/logger.service";
import {VersionCheckService} from "./core/version-check.service";
import {Tenant} from "./shared/models/classes/Tenant";
import {ApiUrlService} from "./shared/services/api-url.service";
import {ForbiddenService} from "./shared/services/forbidden.service";
import {ITenantService} from "./shared/services/itenant.service";
import {TenantFeatureService} from "./shared/services/tenant-feature.service";

const log = new Logger("AppComponent");

@Component({
  selector: "app-root",
  imports: [RouterOutlet],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  protected readonly tenant = signal<Tenant | null>(null);

  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly tenantService = inject(ITenantService);
  private readonly i18nService = inject(I18nService);
  private readonly forbiddenService = inject(ForbiddenService);
  private readonly versionCheckService = inject(VersionCheckService);
  private readonly tenantFeatureService = inject(TenantFeatureService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.tenantService
      .getTenant$()
      .pipe(
        tap((tenant: Tenant) => {
          this.tenant.set(tenant);
          this.apiUrlService.getTenant = () => tenant;

          datadogLogs.setGlobalContext({
            application: `${tenant.tenantName} Web App`,
            tenant: tenant.tenantName,
          });

          datadogRum.setGlobalContext({
            application: `${tenant.tenantName}-webapp`,
            tenant: tenant.tenantName,
            plugin: resolvePluginFromPath(window.location.pathname),
          });
        }),
        switchMap(() => this.tenantFeatureService.load()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: (error: unknown) => log.error("Failed to initialize tenant:", error),
      });

    this.metaService.addTag({
      name: "apple-itunes-app",
      content: `app-id=${environment.appID}, app-argument=${window.location.href}`,
    });

    if (environment.production) {
      Logger.enableProductionMode();
    }

    this.i18nService.init(environment.supportedLanguages);

    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.forbiddenService.allow());

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === "primary"),
        switchMap((route) => route.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        const title = data["title"];
        if (title) {
          this.titleService.setTitle(title);
        }
      });

    this.clearTableRelatedLocalStorage();
    this.versionCheckService.startMonitoring();
  }

  private clearTableRelatedLocalStorage(): void {
    for (const key of Object.keys(localStorage)) {
      if (key.includes("table")) {
        localStorage.removeItem(key);
      }
    }
  }
}
